// src/aws/resourceDispatcher.js
import { fetchLambdaFunctions, createInvocationScanConfig } from './lambdaClient';
import { fetchLexBotsV2, enrichLexBotsWithAnalytics } from './lexV2Client';
import { fetchUnifiedContactFlowErrorMetrics } from './cloudWatchClient';
import { fetchTrustedAdvisorRecommendations } from './trustedAdvisorClient';
import { requestQueueManager } from './requestQueueManager'; // Import for cross-instance state reset
import { 
    fetchAgentStatus, 
    fetchHoursOfOperation, 
    fetchPhoneNumbers, 
    fetchPrompts, 
    fetchQueues, 
    fetchQuickConnects, 
    fetchRoutingProfiles, 
    fetchSecurityProfiles,
    fetchUserHierarchyGroups,
    fetchContactFlows,
    fetchContactFlowModules,
    fetchUsers,
    fetchInstanceConfig } from './connectClient';

const fetchLambdaFunctionsWithOptions = async (credentials, region, instanceId, options = {}) => {
  // Extract any Lambda-specific scanning configuration
  const lambdaScanConfig = options.lambdaScanConfig || createInvocationScanConfig.balanced();
  
  // Call the original function with scan options
  return await fetchLambdaFunctions(credentials, region, lambdaScanConfig);
};

export const resourceFetchers = {
  phoneNumbers: fetchPhoneNumbers,
  hoursOfOperation: fetchHoursOfOperation,
  prompts: fetchPrompts,
  queues: fetchQueues,
  quickConnects: fetchQuickConnects,
  routingProfiles: fetchRoutingProfiles,
  agentStatuses: fetchAgentStatus,
  securityProfiles: fetchSecurityProfiles,
  userHierarchyGroups: fetchUserHierarchyGroups,
  contactFlows: (credentials, region, instanceId, options = {}) =>
    fetchContactFlows(credentials, region, instanceId, options.contactFlowOptions || {}),
  contactFlowModules: (credentials, region, instanceId, options = {}) =>
    fetchContactFlowModules(credentials, region, instanceId, options.contactFlowOptions || {}),
  users: fetchUsers,
  instanceConfig: fetchInstanceConfig,
  functions: fetchLambdaFunctionsWithOptions,
  lexBotV2: async (credentials, region, instanceId, options = {}) => {
    try {
      const bots = await fetchLexBotsV2(credentials, region, instanceId);

      // Validate bots array
      if (!bots || !Array.isArray(bots)) {
        console.warn('fetchLexBotsV2 did not return a valid array:', bots);
        return [];
      }

      // If conversation analytics is enabled, fetch additional data
      if (options.lexV2Options?.enableConversationAnalytics) {
        console.log('Conversation analytics enabled for Lex V2 bots');
        return await enrichLexBotsWithAnalytics(credentials, region, bots);
      }

      return bots;
    } catch (error) {
      console.error('Error in lexBotV2 fetcher:', error);
      return [];
    }
  },
  recommendations: fetchTrustedAdvisorRecommendations,
};

// Helper function to group resources by service tracks
const groupResourcesByTrack = (selectedResourceKeys) => {
  const tracks = {
    connect: [],
    lambda: [],
    lex: [],
    trustedAdvisor: []
  };

  selectedResourceKeys.forEach(resourceKey => {
    if (['agentStatuses', 'hoursOfOperation', 'contactFlows', 'contactFlowModules',
         'phoneNumbers', 'prompts', 'queues', 'quickConnects', 'routingProfiles',
         'securityProfiles', 'users', 'userHierarchyGroups'].includes(resourceKey)) {
      tracks.connect.push(resourceKey);
    } else if (resourceKey === 'functions') {
      tracks.lambda.push(resourceKey);
    } else if (resourceKey === 'lexBotV2') {
      tracks.lex.push(resourceKey);
    } else if (resourceKey === 'recommendations') {
      tracks.trustedAdvisor.push(resourceKey);
    }
  });

  return tracks;
};

export const fetchResources = async ({
  selectedResourceKeys,
  selectedInstanceIds,
  awsCredentials,
  onProgress = () => {},
  lambdaOptions = {},
  contactFlowOptions = {},
  lexV2Options = {},
  userOptions = {}
}) => {
  const region = awsCredentials.region;
  const results = {};

  // Reset rate limiting monitoring stats for new export operation
  requestQueueManager.resetMonitoringStats();

  for (const instanceId of selectedInstanceIds) {
    results[instanceId] = {}; // Initialize container for each instance
  }

  // Group resources by service tracks for concurrent processing
  const tracks = groupResourcesByTrack(selectedResourceKeys);
  const needsInstanceConfig = selectedResourceKeys.length > 0;

  // TRACK 1: Amazon Connect Processing (Instance Config + Connect Resources)
  const processConnectTrack = async () => {
    if (!needsInstanceConfig && tracks.connect.length === 0) return;

    // Phase 1a: Instance Configuration (sequential prerequisite)
    if (needsInstanceConfig) {
      await processInstanceConfig();
    }

    // Phase 1b: Connect Resources (sequential within Connect rate limits)
    if (tracks.connect.length > 0) {
      await processConnectResourcesSequentially();
    }
  };

  const processInstanceConfig = async () => {
    const resourceKey = 'instanceConfig';
    let totalResourceCount = 0;
    let exportedResourceCount = 0;
    let errorCount = 0;
    let completedInstances = 0;

    onProgress(resourceKey, {
      state: 'loading',
      exported: 0,
      total: 0,
      errors: 0,
      instancesCompleted: 0,
      totalInstances: selectedInstanceIds.length
    });

    for (const instanceId of selectedInstanceIds) {
      try {
        const data = await fetchInstanceConfig(awsCredentials, region, instanceId);
        results[instanceId][resourceKey] = data;

        totalResourceCount += 1;
        exportedResourceCount += 1;
        completedInstances++;

        onProgress(resourceKey, {
          state: 'loading',
          exported: exportedResourceCount,
          total: totalResourceCount,
          errors: errorCount,
          instancesCompleted: completedInstances,
          totalInstances: selectedInstanceIds.length
        });
      } catch (error) {
        console.error(`Failed to fetch ${resourceKey} for ${instanceId}`, error);
        results[instanceId][resourceKey] = { error: error.message };

        completedInstances++;
        errorCount++;
        totalResourceCount += 1;

        onProgress(resourceKey, {
          state: 'loading',
          exported: exportedResourceCount,
          total: totalResourceCount,
          errors: errorCount,
          instancesCompleted: completedInstances,
          totalInstances: selectedInstanceIds.length
        });
      }
    }

    const finalState = errorCount > 0 ? 'error' : 'success';
    onProgress(resourceKey, {
      state: finalState,
      exported: exportedResourceCount,
      total: totalResourceCount,
      errors: errorCount,
      instancesCompleted: completedInstances,
      totalInstances: selectedInstanceIds.length
    });
  };

  const processConnectResourcesSequentially = async () => {
    // Process all Connect resources sequentially to respect Connect API rate limits
    for (const resourceKey of tracks.connect) {
      await processResourceAcrossInstances(resourceKey, { contactFlowOptions, userOptions });
    }
  };

  // TRACK 2: Lambda Processing
  const processLambdaTrack = async () => {
    if (tracks.lambda.length === 0) return;

    for (const resourceKey of tracks.lambda) {
      await processAccountWideResource(resourceKey, { lambdaScanConfig: lambdaOptions });
    }
  };

  // TRACK 3: Lex Processing (Lex V2)
  const processLexTrack = async () => {
    if (tracks.lex.length === 0) return;

    for (const resourceKey of tracks.lex) {
      await processAccountWideResource(resourceKey, { lexV2Options });
    }
  };

  // TRACK 4: Trusted Advisor Processing
  const processTrustedAdvisorTrack = async () => {
    if (tracks.trustedAdvisor.length === 0) return;

    for (const resourceKey of tracks.trustedAdvisor) {
      await processAccountWideResource(resourceKey, {});
    }
  };

  // Helper function to process resources across all instances
  const processResourceAcrossInstances = async (resourceKey, options = {}) => {
    const fetcher = resourceFetchers[resourceKey];
    if (!fetcher) return;

    let totalResourceCount = 0;
    let exportedResourceCount = 0;
    let errorCount = 0;
    let completedInstances = 0;

    onProgress(resourceKey, {
      state: 'loading',
      exported: 0,
      total: 0,
      errors: 0,
      instancesCompleted: 0,
      totalInstances: selectedInstanceIds.length
    });

    for (const instanceId of selectedInstanceIds) {
      if (completedInstances > 0) {
        console.log(`🔄 Resetting circuit breakers for instance ${instanceId} processing ${resourceKey}`);
        requestQueueManager.resetForNewInstance();
      }

      try {
        let data;

        if (resourceKey === 'lexBotV2') {
          data = await fetcher(awsCredentials, region, instanceId, options);
        } else {
          data = await fetcher(awsCredentials, region, instanceId, options);
        }

        results[instanceId][resourceKey] = data;

        let resourceCount = 0;
        if (Array.isArray(data)) {
          resourceCount = data.length;
        } else if (data && typeof data === 'object' && !data.error) {
          resourceCount = 1;
        }

        totalResourceCount += resourceCount;
        exportedResourceCount += resourceCount;
        completedInstances++;

        onProgress(resourceKey, {
          state: 'loading',
          exported: exportedResourceCount,
          total: totalResourceCount,
          errors: errorCount,
          instancesCompleted: completedInstances,
          totalInstances: selectedInstanceIds.length
        });
      } catch (error) {
        console.error(`Failed to fetch ${resourceKey} for ${instanceId}`, error);
        results[instanceId][resourceKey] = { error: error.message };

        completedInstances++;
        errorCount++;
        totalResourceCount += 1;

        onProgress(resourceKey, {
          state: 'loading',
          exported: exportedResourceCount,
          total: totalResourceCount,
          errors: errorCount,
          instancesCompleted: completedInstances,
          totalInstances: selectedInstanceIds.length
        });
      }
    }

    const finalState = errorCount > 0 ? 'error' : 'success';
    onProgress(resourceKey, {
      state: finalState,
      exported: exportedResourceCount,
      total: totalResourceCount,
      errors: errorCount,
      instancesCompleted: completedInstances,
      totalInstances: selectedInstanceIds.length
    });
  };

  // Helper function to process account-wide resources (Lambda, Trusted Advisor)
  const processAccountWideResource = async (resourceKey, options = {}) => {
    const fetcher = resourceFetchers[resourceKey];
    if (!fetcher) return;

    let totalResourceCount = 0;
    let exportedResourceCount = 0;
    let errorCount = 0;
    let completedInstances = 0;

    onProgress(resourceKey, {
      state: 'loading',
      exported: 0,
      total: 0,
      errors: 0,
      instancesCompleted: 0,
      totalInstances: selectedInstanceIds.length
    });

    try {
      let data;

      if (resourceKey === 'functions') {
        data = await fetcher(awsCredentials, region, selectedInstanceIds[0], options);
      } else if (resourceKey === 'recommendations') {
        data = await fetcher(awsCredentials, region);
      } else if (resourceKey === 'lexBotV2') {
        data = await fetcher(awsCredentials, region, selectedInstanceIds[0], options);
      }

      // Apply to all instances since it's account-wide
      selectedInstanceIds.forEach(id => {
        results[id][resourceKey] = data;
      });

      let resourceCount = 0;
      if (Array.isArray(data)) {
        resourceCount = data.length;
      } else if (data && typeof data === 'object' && !data.error) {
        resourceCount = 1;
      }

      totalResourceCount = resourceCount;
      exportedResourceCount = resourceCount;
      completedInstances = selectedInstanceIds.length;

    } catch (error) {
      console.error(`Failed to fetch ${resourceKey}:`, error);
      selectedInstanceIds.forEach(id => {
        results[id][resourceKey] = { error: error.message };
      });

      errorCount++;
      completedInstances = selectedInstanceIds.length;
      totalResourceCount = 1;
    }

    const finalState = errorCount > 0 ? 'error' : 'success';
    onProgress(resourceKey, {
      state: finalState,
      exported: exportedResourceCount,
      total: totalResourceCount,
      errors: errorCount,
      instancesCompleted: completedInstances,
      totalInstances: selectedInstanceIds.length
    });
  };

  // Execute all 4 tracks concurrently using Promise.allSettled
  console.log('🚀 Starting concurrent processing across 4 service tracks...');

  const trackPromises = [
    processConnectTrack(),     // Track 1: Instance Config → Connect Resources
    processLambdaTrack(),      // Track 2: Lambda Functions
    processLexTrack(),         // Track 3: Lex V2 → V1
    processTrustedAdvisorTrack() // Track 4: Trusted Advisor
  ];

  const trackResults = await Promise.allSettled(trackPromises);

  // Log any track failures for debugging
  trackResults.forEach((result, index) => {
    const trackNames = ['Connect', 'Lambda', 'Lex', 'Trusted Advisor'];
    if (result.status === 'rejected') {
      console.error(`❌ Track ${index + 1} (${trackNames[index]}) failed:`, result.reason);
    } else {
      console.log(`✅ Track ${index + 1} (${trackNames[index]}) completed successfully`);
    }
  });

  console.log('✨ All service tracks completed!');

  // Generate comprehensive rate limiting performance report
  console.log('\n📊 Generating rate limiting performance report...');
  requestQueueManager.logPerformanceReport();

  // Apply unified error metrics for Contact Flows and Modules (if either is selected)
  const hasContactFlows = selectedResourceKeys.includes('contactFlows');
  const hasContactFlowModules = selectedResourceKeys.includes('contactFlowModules');

  if (hasContactFlows || hasContactFlowModules) {
    console.log('🔄 Applying unified Contact Flow/Module error metrics...');

    for (const instanceId of selectedInstanceIds) {
      try {
        const instanceData = results[instanceId];
        const contactFlows = instanceData.contactFlows || [];
        const contactFlowModules = instanceData.contactFlowModules || [];

        // Only call if we have flows or modules to process
        if (contactFlows.length > 0 || contactFlowModules.length > 0) {
          const { contactFlows: updatedFlows, contactFlowModules: updatedModules } =
            await fetchUnifiedContactFlowErrorMetrics(
              awsCredentials,
              region,
              contactFlows,
              contactFlowModules,
              instanceId
            );

          // Update the results with error metrics applied
          if (hasContactFlows) {
            results[instanceId].contactFlows = updatedFlows;
          }
          if (hasContactFlowModules) {
            results[instanceId].contactFlowModules = updatedModules;
          }
        }
      } catch (error) {
        console.error(`Failed to apply unified error metrics for instance ${instanceId}:`, error);
        // Continue without error metrics - they'll remain as 0
      }
    }
  }

  return results;
};